import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import { providerLabel } from '../../utils/crmContext';
import '../../styles/components/crmPrefillTag.less';

// The per-field provenance tag (MS-6627, T6.2): "from OutPerform", beside the label of a field the
// CRM link pre-filled, with a mark for whether that link was verified. It renders one entry of the
// form's `crmFields` — `{ source, verified, locked }`, written by prefillProvenance when the
// pre-fill lands and dropped by dropProvenance the moment the salesperson edits the field — and
// decides nothing itself: no provenance, no tag.
//
// This is the answer to the finding that an IJM agent could not tell that name and mobile had come
// from Salesforce. It has to work for the legacy `book_*` link from day one, which is why the
// source is whatever provider name book-api reported and the label is a lookup, never a branch.
//
// `verified` is a boolean the provenance already decided with presence semantics (sessionVerified);
// the tag only reads it. `locked` means the field is read-only because the CRM owns the value, and
// the tag says so — the buyer's fields never are; the CRM's own source (`crmSource`, MS-6628) is.
const ns = { ns: TRANSLATION_FILES.translation };

export function CrmPrefillTag({ provenance, field, t }) {
  if (!provenance || !provenance.source) return null;
  const crm = providerLabel(provenance.source);
  const verified = provenance.verified === true;
  const state = t(verified ? 'verified' : 'unverified', ns);
  const lockedWord = provenance.locked ? `, ${t('locked', ns)}` : '';
  // The accessible name says the whole thing in one phrase — "Pre-filled from OutPerform, verified"
  // — so a screen reader does not read an icon name and a fragment.
  const ariaLabel = `${t('Pre-filled from {{crm}}', { ...ns, crm })}, ${state}${lockedWord}`;
  return (
    <span
      className={`crm-prefill-tag crm-prefill-tag--${verified ? 'verified' : 'unverified'}${provenance.locked ? ' crm-prefill-tag--locked' : ''}`}
      role="img"
      aria-label={ariaLabel}
      title={ariaLabel}
      data-testid={`crm-prefill-tag${field ? `-${field}` : ''}`}
      data-source={provenance.source}
      data-verified={verified ? 'true' : 'false'}
    >
      <Icon name={verified ? 'check circle' : 'warning circle'} aria-hidden="true" />
      <span className="crm-prefill-tag__text">{t('from {{crm}}', { ...ns, crm })}</span>
      {provenance.locked ? <Icon name="lock" aria-hidden="true" /> : null}
    </span>
  );
}

CrmPrefillTag.propTypes = {
  provenance: PropTypes.shape({
    source: PropTypes.string,
    verified: PropTypes.bool,
    locked: PropTypes.bool,
  }),
  field: PropTypes.string,
  t: PropTypes.func.isRequired,
};

CrmPrefillTag.defaultProps = {
  provenance: null,
  field: '',
};

export default withTranslation(namespaces)(CrmPrefillTag);



// WEBPACK FOOTER //
// ./src/components/Crm/CrmPrefillTag.jsx